/**
 * Coarse on-glass states shared by the player and Studio.
 * Specific idle causes (geo, aspect, low capacity) stay on the device and are not this enum.
 */
export const playerGlassStates = [
  'unpaired',
  'waiting_for_publication',
  'playing_partial_cache'
] as const

export type TPlayerGlassState = (typeof playerGlassStates)[number]

export type TPlayerGlassLocale = 'pt' | 'en'

const HEADLINES: Record<TPlayerGlassState, Record<TPlayerGlassLocale, string>> = {
  unpaired: {
    pt: 'Aguardando pareamento',
    en: 'Waiting to pair'
  },
  waiting_for_publication: {
    pt: 'Aguardando conteúdo',
    en: 'Waiting for content'
  },
  playing_partial_cache: {
    pt: 'A reproduzir o que já está em cache',
    en: 'Playing cached content'
  }
}

const DETAILS: Record<TPlayerGlassState, Record<TPlayerGlassLocale, string | null>> = {
  unpaired: { pt: null, en: null },
  waiting_for_publication: {
    pt: 'O conteúdo entra na rotação quando o download termina.',
    en: 'Content joins the loop when the download finishes.'
  },
  playing_partial_cache: {
    pt: 'Itens em falta ficam de fora até o ficheiro estar completo.',
    en: 'Missing items stay out until the file is complete.'
  }
}

export function playerGlassLocale(locale: string | null | undefined): TPlayerGlassLocale {
  return (locale ?? '').toLowerCase().startsWith('pt') ? 'pt' : 'en'
}

export function playerGlassStateHeadline(
  state: TPlayerGlassState,
  locale: TPlayerGlassLocale
): string {
  return HEADLINES[state][locale]
}

export function playerGlassStateDetail(
  state: TPlayerGlassState,
  locale: TPlayerGlassLocale
): string | null {
  return DETAILS[state][locale]
}

export function resolvePlayerGlassState(input: {
  paired: boolean
  playableCount: number
  skippedUncachedCount: number
  hasPublishedSchedule: boolean
}): TPlayerGlassState | null {
  if (!input.paired) {
    return 'unpaired'
  }
  if (input.playableCount > 0 && input.skippedUncachedCount > 0) {
    return 'playing_partial_cache'
  }
  if (
    input.playableCount === 0
    && (input.skippedUncachedCount > 0 || !input.hasPublishedSchedule)
  ) {
    return 'waiting_for_publication'
  }
  return null
}
